/**
 * IndexedDB Service for High-Scale Offline Storage (100,000+ records)
 * Zero external dependencies, pure native browser IndexedDB API.
 */

const DB_NAME = "MTProductsDB";
const DB_VERSION = 1;
const STORE_PRODUCTS = "products";
const STORE_META = "meta";

let dbInstance = null;

/**
 * Open and initialize the IndexedDB instance
 */
export const openDB = () => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      return resolve(dbInstance);
    }

    if (!window.indexedDB) {
      console.warn("IndexedDB not supported in this browser. Falling back to in-memory.");
      return resolve(null);
    }

    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Products store (keyed by product id)
      if (!db.objectStoreNames.contains(STORE_PRODUCTS)) {
        const productStore = db.createObjectStore(STORE_PRODUCTS, { keyPath: "id" });
        productStore.createIndex("productName", "productName", { unique: false });
        productStore.createIndex("modifiedOn", "modifiedOn", { unique: false });
      }

      // Metadata store (lastSync, counts, etc.)
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: "key" });
      }
    };

    request.onsuccess = (event) => {
      dbInstance = event.target.result;
      resolve(dbInstance);
    };

    request.onerror = (event) => {
      console.error("IndexedDB open error:", event.target.error);
      reject(event.target.error);
    };
  });
};

/**
 * Get all cached products from IndexedDB (instant 0-100ms load)
 */
export const getAllProductsFromDB = async () => {
  try {
    const db = await openDB();
    if (!db) return [];

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_PRODUCTS], "readonly");
      const store = transaction.objectStore(STORE_PRODUCTS);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = (e) => {
        console.error("Error fetching products from DB", e);
        reject(e);
      };
    });
  } catch (err) {
    console.error("getAllProductsFromDB error:", err);
    return [];
  }
};

/**
 * Save products into IndexedDB
 * Handles bulk batching for 100,000+ items cleanly without freezing the browser
 */
export const saveProductsToDB = async (products, isDelta = false, serverTime = null) => {
  try {
    const db = await openDB();
    if (!db || !Array.isArray(products) || products.length === 0) return;

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_PRODUCTS, STORE_META], "readwrite");
      const productStore = transaction.objectStore(STORE_PRODUCTS);
      const metaStore = transaction.objectStore(STORE_META);

      // If full sync (not delta), clear old cached products first
      if (!isDelta) {
        productStore.clear();
      }

      // Add or update records
      for (let i = 0; i < products.length; i++) {
        const item = products[i];
        if (item && item.id) {
          // Pre-generate fast lowercase search key and special-char-stripped key
          const raw = `${item.productName || ""} ${item.designNumber || ""}`.toLowerCase();
          item._searchKey = raw;
          item._cleanKey = raw.replace(/[^a-z0-9]/g, "");
          productStore.put(item);
        }
      }

      // Update sync metadata
      if (serverTime) {
        metaStore.put({ key: "lastSyncTime", value: serverTime });
      }

      transaction.oncomplete = () => {
        resolve(true);
      };

      transaction.onerror = (e) => {
        console.error("Error saving products to DB", e);
        reject(e);
      };
    });
  } catch (err) {
    console.error("saveProductsToDB error:", err);
  }
};

/**
 * Save a single product (used on Add / Edit)
 */
export const upsertSingleProductInDB = async (product) => {
  try {
    const db = await openDB();
    if (!db || !product || !product.id) return;

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_PRODUCTS], "readwrite");
      const store = transaction.objectStore(STORE_PRODUCTS);
      
      const raw = `${product.productName || ""} ${product.designNumber || ""}`.toLowerCase();
      const item = {
        ...product,
        _searchKey: raw,
        _cleanKey: raw.replace(/[^a-z0-9]/g, ""),
      };
      
      store.put(item);

      transaction.oncomplete = () => resolve(true);
      transaction.onerror = (e) => reject(e);
    });
  } catch (err) {
    console.error("upsertSingleProductInDB error:", err);
  }
};

/**
 * Delete a single product from IndexedDB
 */
export const deleteSingleProductFromDB = async (id) => {
  try {
    const db = await openDB();
    if (!db || !id) return;

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_PRODUCTS], "readwrite");
      const store = transaction.objectStore(STORE_PRODUCTS);
      store.delete(id);

      transaction.oncomplete = () => resolve(true);
      transaction.onerror = (e) => reject(e);
    });
  } catch (err) {
    console.error("deleteSingleProductFromDB error:", err);
  }
};

/**
 * Delete multiple products from IndexedDB by IDs
 */
export const deleteMultipleProductsFromDB = async (ids) => {
  try {
    const db = await openDB();
    if (!db || !ids || (Array.isArray(ids) && ids.length === 0)) return;

    const idList = Array.isArray(ids) ? ids : Array.from(ids);

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_PRODUCTS], "readwrite");
      const store = transaction.objectStore(STORE_PRODUCTS);
      for (const id of idList) {
        if (id) store.delete(id);
      }

      transaction.oncomplete = () => resolve(true);
      transaction.onerror = (e) => reject(e);
    });
  } catch (err) {
    console.error("deleteMultipleProductsFromDB error:", err);
  }
};

/**
 * Get last sync timestamp for incremental / delta sync
 */
export const getLastSyncTimeFromDB = async () => {
  try {
    const db = await openDB();
    if (!db) return null;

    return new Promise((resolve) => {
      const transaction = db.transaction([STORE_META], "readonly");
      const store = transaction.objectStore(STORE_META);
      const request = store.get("lastSyncTime");

      request.onsuccess = () => {
        resolve(request.result?.value || null);
      };

      request.onerror = () => {
        resolve(null);
      };
    });
  } catch (err) {
    console.error("getLastSyncTimeFromDB error:", err);
    return null;
  }
};

/**
 * Get total cached products count
 */
export const getProductsCountFromDB = async () => {
  try {
    const db = await openDB();
    if (!db) return 0;

    return new Promise((resolve) => {
      const transaction = db.transaction([STORE_PRODUCTS], "readonly");
      const store = transaction.objectStore(STORE_PRODUCTS);
      const request = store.count();

      request.onsuccess = () => resolve(request.result || 0);
      request.onerror = () => resolve(0);
    });
  } catch (err) {
    return 0;
  }
};

/**
 * Get all items created or edited while offline
 */
export const getPendingSyncProductsFromDB = async () => {
  try {
    const all = await getAllProductsFromDB();
    return all.filter((p) => p && p._pendingSync === true);
  } catch (err) {
    console.error("getPendingSyncProductsFromDB error:", err);
    return [];
  }
};
